/*
 * Sale mode.
 *
 * Three jobs, all switched on from Theme settings > Sale mode:
 *
 *   1. <sale-countdown> -- the announcement bar countdown.
 *   2. The notification popup in the bottom right corner.
 *   3. The cart drawer bridge: the sale message and the stapelkorting tier
 *      message are server rendered into the theme's own cart drawer, but the
 *      live cart is the Rebuy smart cart, which builds itself in the browser
 *      out of its own data. This pulls sections/sale-mode-cart.liquid through
 *      the Section Rendering API and drops it into the flyout, so both carts
 *      show the same messages off the same Liquid.
 *
 * Loaded from layout/theme.liquid, only while sale mode is enabled.
 */

/* 1. Announcement bar countdown ------------------------------------------- */

/*
 * Deliberately not reusing <promo-countdown>: that element hides its own
 * .promo-banner wrapper when the campaign ends and drives promo-banner
 * classnames, neither of which applies inside the announcement bar.
 *
 * data-end is an absolute timestamp with the store's UTC offset, built in
 * sale-mode-announcement.liquid, so the sale ends at the store's closing time
 * rather than at each visitor's own midnight.
 */
if (!customElements.get('sale-countdown')) {
  class SaleCountdown extends HTMLElement {
    connectedCallback() {
      this.endTime = Date.parse(this.dataset.end);

      if (Number.isNaN(this.endTime)) {
        this.hidden = true;
        return;
      }

      this.units = {
        days: this.querySelector('[data-unit="days"]'),
        hours: this.querySelector('[data-unit="hours"]'),
        minutes: this.querySelector('[data-unit="minutes"]'),
        seconds: this.querySelector('[data-unit="seconds"]')
      };

      this.tick();
      this.timer = setInterval(this.tick.bind(this), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const distance = this.endTime - Date.now();

      if (distance <= 0) {
        clearInterval(this.timer);
        this.render(0, 0, 0, 0);
        return;
      }

      const seconds = Math.floor(distance / 1000);
      this.render(
        Math.floor(seconds / 86400),
        Math.floor((seconds % 86400) / 3600),
        Math.floor((seconds % 3600) / 60),
        seconds % 60
      );
    }

    render(days, hours, minutes, seconds) {
      /*
       * The bar is one line on mobile, so the days drop out of the way once
       * they are spent rather than sitting there reading "00d".
       */
      this.setUnit(this.units.days, days, days === 0);
      this.setUnit(this.units.hours, hours);
      this.setUnit(this.units.minutes, minutes);
      this.setUnit(this.units.seconds, seconds);
    }

    setUnit(element, value, hide = false) {
      if (!element) return;

      element.hidden = hide;

      const separator = element.nextElementSibling;
      if (separator && separator.classList.contains('sale-countdown__sep')) {
        separator.hidden = hide;
      }

      if (hide) return;

      const target = element.querySelector('[data-value]');
      const formatted = String(value).padStart(2, '0');
      if (target && target.textContent !== formatted) {
        target.textContent = formatted;
      }
    }
  }

  customElements.define('sale-countdown', SaleCountdown);
}

/* 2. Notification popup --------------------------------------------------- */

(function salePopup() {
  const popup = document.querySelector('[data-sale-popup]');
  if (!popup) return;

  const delay = (parseFloat(popup.dataset.delay) || 0) * 1000;
  const duration = (parseFloat(popup.dataset.duration) || 8) * 1000;
  const frequency = (parseFloat(popup.dataset.frequency) || 0) * 1000;

  let hideTimer;
  let cycleTimer;
  let dismissed = false;

  function show() {
    if (dismissed) return;

    popup.hidden = false;
    /* Force a reflow so the transition runs from the hidden state. */
    void popup.offsetWidth;
    popup.classList.add('is-visible');

    clearTimeout(hideTimer);
    hideTimer = setTimeout(hide, duration);
  }

  function hide() {
    popup.classList.remove('is-visible');
  }

  /*
   * Closing is for this page view only, the way it works on the other store:
   * the popup comes back on the next page, which is the point of a campaign
   * nudge. Nothing is stored, so nothing has to be cleaned up after the sale.
   */
  function dismiss() {
    dismissed = true;
    clearTimeout(hideTimer);
    clearInterval(cycleTimer);
    hide();
  }

  /* Taken out of the layout again once it has faded, not while it fades. */
  popup.addEventListener('transitionend', () => {
    if (!popup.classList.contains('is-visible')) popup.hidden = true;
  });

  popup.querySelector('[data-sale-popup-close]')?.addEventListener('click', dismiss);

  setTimeout(() => {
    show();
    if (frequency > 0) cycleTimer = setInterval(show, frequency);
  }, delay);
})();

/* 3. Cart drawer bridge --------------------------------------------------- */

(function saleModeCart() {
  /*
   * The theme's own drawer renders these messages itself, from
   * sections/cart-drawer.liquid, and re-renders on every cart change. Its
   * presence is the signal that the smart cart is not the active cart, so
   * there is nothing to bridge.
   */
  if (document.querySelector('cart-drawer')) return;

  const SECTION = 'sale-mode-cart';
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* null until the first response: "not known yet", as opposed to "no message". */
  let html = null;
  let observing = false;
  let pending;

  /*
   * Straight into the item list rather than above it. On desktop Rebuy indents
   * .rebuy-cart__flyout-items to clear the recommendation column next to it
   * (see cart-drawer.css), and a sibling placed before it would sit under that
   * column instead of over the items.
   */
  function inject() {
    if (html === null) return;

    const list = document.querySelector('.rebuy-cart__flyout-items');
    if (!list) return;

    const existing = list.querySelector('[data-sale-mode-cart]');

    if (html === '') {
      existing?.remove();
      return;
    }

    /*
     * Idempotent on purpose: the observer below calls this after every mutation
     * of the flyout, including the ones this makes, and an unchanged node is
     * left alone so nothing loops.
     */
    if (existing) {
      if (existing.innerHTML !== html) existing.innerHTML = html;
      return;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'sale-mode-cart';
    wrapper.setAttribute('data-sale-mode-cart', '');
    wrapper.innerHTML = html;
    list.prepend(wrapper);
  }

  async function fetchMessages() {
    try {
      /*
       * Cache busted: this depends on the cart, and the response has to be the
       * cart as it is right now, not as it was when the tier last changed.
       */
      const response = await fetch(`${ROOT}?section_id=${SECTION}&t=${Date.now()}`, {
        cache: 'no-store'
      });
      if (!response.ok) return;

      const markup = await response.text();
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const node = parsed.querySelector('[data-sale-mode-cart]');

      html = node ? node.innerHTML.trim() : '';
      inject();
    } catch (error) {
      /* A failed refresh leaves the last messages in place; never break the cart. */
    }
  }

  /*
   * Rebuy re-renders the item list from scratch on quantity changes, which
   * takes the injected node with it, and the cart events do not all land after
   * that has happened. Watching the flyout puts the messages back either way.
   */
  function observe() {
    const cart = document.getElementById('rebuy-cart');
    if (!cart || observing) return;

    observing = true;
    new MutationObserver(inject).observe(cart, { childList: true, subtree: true });
  }

  /*
   * Several of these fire together on a single add, so the work is coalesced.
   * The wait also lets Rebuy's own call to the cart API land first, so the
   * section is rendered against the cart the visitor just changed rather than
   * the one it was a moment ago.
   */
  function refresh() {
    clearTimeout(pending);
    pending = setTimeout(() => {
      observe();
      fetchMessages();
    }, 150);
  }

  ['rebuy:smartcart.show', 'rebuy:cart.add', 'rebuy:cart.change', 'rebuy:cart.update'].forEach(
    (event) => document.addEventListener(event, refresh)
  );
})();
